import { useContext, useRef, type MouseEvent as ReactMouseEvent } from 'react';

import { MarkerEventHandlersContext } from '../contexts/MarkerEventHandlers.context.js';
import { TrackEventHandlersContext } from '../contexts/TrackEventHandlers.context.js';

export const useAnnotationsHandlers = () => {
  const markerEventHandlers = useContext(MarkerEventHandlersContext);
  const trackEventHandlers = useContext(TrackEventHandlersContext);

  const currentHoveredTrack = useRef<number | undefined>(undefined);
  const currentHoveredMarker = useRef<string | undefined>(undefined);

  /**
   * Hovers a marker, which will trigger:
   * - onMouseEnter if it's the first time
   * - onMouseMove if it was already hovered
   *
   * As a side effect the track will blur.
   * If there was a marker previously hovered, it won't trigger mouseLeave and
   * will trigger directly mouseEnter of the new marker
   *
   * @param e -
   * @param markerId -
   */
  const hoverMarker = (e: ReactMouseEvent, markerId: string) => {
    // if it's the first time that the marker is hovered
    if (markerId !== currentHoveredMarker.current) {
      currentHoveredMarker.current = markerId;
      markerEventHandlers?.[markerId]?.onMouseEnter?.(e);

      // when hovering a marker, the track is not hovered
      blurTrack();
    } else {
      markerEventHandlers?.[markerId].onMouseMove?.(e);
    }
  };

  /**
   * Blurs a marker, which will trigger:
   * - onMouseLeave
   *
   * @param e -
   * @param markerId -
   */
  const blurMarker = (e: ReactMouseEvent, markerId: string) => {
    markerEventHandlers?.[markerId]?.onMouseLeave?.(e);

    currentHoveredMarker.current = undefined;
  };

  /**
   * Clicks a marker, which will trigger:
   * - onClick
   *
   * @param e -
   * @param markerId -
   */
  const clickMarker = (e: ReactMouseEvent, markerId: string) => {
    markerEventHandlers?.[markerId]?.onClick?.(e);
  };

  /**
   * Hovers a track, which will trigger:
   * - onMouseEnter
   *
   * @param e -
   * @param trackIndex -
   */
  const hoverTrack = (e: ReactMouseEvent, trackIndex: number) => {
    currentHoveredTrack.current = trackIndex;

    trackEventHandlers?.[trackIndex]?.onMouseEnter?.(e);
  };

  /**
   * Blurs a track, which won't trigger any event
   */
  const blurTrack = () => {
    currentHoveredTrack.current = undefined;
  };

  return {
    hoverTrack,
    blurTrack,
    hoverMarker,
    blurMarker,
    clickMarker,
    currentHoveredTrack,
    currentHoveredMarker,
  };
};
